/** @jsx jsx */
import { jsx, Box, Flex } from 'theme-ui';
import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import Button from '../components/Button';
import { locNavigate } from '../state/session';
import { ButtonBox } from './utils';
import { BuyContactDeliveryIcon, ChangeToPickupPointIcon, ChooseTimeIcon, NotReceiveIcon } from '../components/Icon';

// values understood by the `action` query parameter of the /sovijakelu page
const ACTION = {
  CHOOSE_TIME: 'time',
  CHANGE_TO_PICKUP_POINT: 'service-point',
  NOT_RECEIVE: 'not-receive',
  DELIVERY_UNATTENDED: 'delivery-unattended',
  BUY_CONTACT_DELIVERY: 'buy-contact-delivery',
};

export const HomeDeliveryActions = ({ shipment }) => {
  const dispatch = useDispatch();
  const { t: translate } = useTranslation();
  const { shipmentNumber, deliveryTimeToken } = shipment || {};
  const [selectedOption, setSelectedOption] = useState(null);

  const options = useMemo(
    () =>
      [
        shipment?.deliveryTimeAgreeable && {
          value: ACTION.CHOOSE_TIME,
          icon: ChooseTimeIcon,
          name: translate('delivery.agree.homeDeliveryActions.chooseTime'),
        },
        shipment?.canTransferDeliveryToServicePoint && {
          value: ACTION.CHANGE_TO_PICKUP_POINT,
          icon: ChangeToPickupPointIcon,
          name: translate('delivery.agree.homeDeliveryActions.changeToPickupPoint'),
        },
        shipment?.canBuyContactDelivery && {
          value: ACTION.BUY_CONTACT_DELIVERY,
          icon: BuyContactDeliveryIcon,
          name: translate('delivery.agree.homeDeliveryActions.buyContactDelivery'),
        },
        shipment?.canNotReceive && {
          value: ACTION.NOT_RECEIVE,
          icon: NotReceiveIcon,
          name: translate('delivery.agree.homeDeliveryActions.notReceive'),
        },
      ].filter(Boolean),
    [shipment, translate]
  );

  const handleSubmit = useCallback(() => {
    if (!selectedOption) {
      return;
    }
    const params = `?shipment=${shipmentNumber}&token=${deliveryTimeToken}&action=${selectedOption}`;
    dispatch(locNavigate('/sovijakelu', params));
  }, [dispatch, selectedOption, shipmentNumber, deliveryTimeToken]);

  if (!options.length) {
    return null;
  }

  return (
    <Box sx={{ mb: 4 }}>
      <p sx={{ fontWeight: 'semibold' }}>{translate('delivery.agree.selectDelivery')}</p>
      {options.map((option) => (
        <Option key={option.value} selectedOption={selectedOption} onChange={setSelectedOption} {...option} />
      ))}

      <Box sx={{ mt: 4, backgroundColor: 'lightYellow', px: 2, py: 1, borderRadius: 1 }}>
        <p sx={{ ml: 2, mr: 2 }}>{translate('delivery.agree.selectionBinding')}</p>
      </Box>
      <ButtonBox>
        <Button sx={{ mt: 4 }} onClick={handleSubmit} disabled={!selectedOption}>
          {translate('delivery.agree.continue')}
        </Button>
      </ButtonBox>
    </Box>
  );
};

const Option = ({ selectedOption, onChange, icon: IconComponent, name, value }) => {
  return (
    <Box
      role="radio"
      tabIndex={0}
      onClick={() => onChange(value)}
      aria-checked={selectedOption === value}
      onKeyPress={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onChange(value);
        }
      }}
      sx={{
        borderStyle: 'solid',
        borderColor: selectedOption === value ? 'primary' : 'border',
        alignItems: 'center',
        cursor: 'pointer',
        borderWidth: 2,
        borderRadius: 1,
        px: 3,
        py: 2,
        my: 2,
        flex: '1 1 auto',
      }}
    >
      <Flex sx={{ alignItems: 'center' }}>
        {IconComponent && (
          <Box sx={{ color: 'primary', flex: 'none', mr: 3 }}>
            <IconComponent aria-hidden="true" focusable="false" />
          </Box>
        )}
        <p sx={{ fontWeight: selectedOption === value ? 'bold' : 'normal', m: 0 }}>{name}</p>
      </Flex>
    </Box>
  );
};

export default HomeDeliveryActions;
